import { IPMAStation } from "@/types/ipma-station/ipma-station.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pencil, Archive, ArchiveRestore } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const useIPMAStationColumns = (
  t: (key: string) => string,
  onEdit: (station: IPMAStation) => void,
  onArchiveAction: (station: IPMAStation) => void,
  infoSourceMap: Record<string, string> = {},
  basins: { id: string; name: string }[],
): Column<IPMAStation>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<IPMAStation>[] = [
    {
      header: t("ipmaStations.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("ipmaStations.table.basin"),
      accessorKey: "basinId",
      sortable: true,
      className: "min-w-[180px]",
      cell: (_: string, row: IPMAStation) => (
        <div>{row.basinId ? basins.find((basin) => basin.id === row.basinId)?.name : ""}</div>
      ),
    },
    {
      header: t("ipmaStations.table.precipitationRate"),
      accessorKey: "precipitationRate",
      sortable: true,
      className: "min-w-[150px]",
      cell: (_: string, row: IPMAStation) => (
        <div>{row.precipitationRate !== undefined ? formatNumberDynamic(row.precipitationRate) : "-"}</div>
      ),
    },
    {
      header: t("ipmaStations.table.precipitationSource"),
      accessorKey: "precipitationSourceId",
      sortable: false,
      className: "min-w-[180px]",
      cell: (_: string, row: IPMAStation) => (
        <div>{infoSourceMap[row.precipitationSourceId] || ""}</div>
      ),
    },
    {
      header: t("ipmaStations.table.evaporationRate"),
      accessorKey: "evaporationRate",
      sortable: true,
      className: "min-w-[150px]",
      cell: (_: string, row: IPMAStation) => (
        <div>{row.evaporationRate !== undefined ? formatNumberDynamic(row.evaporationRate) : "-"}</div>
      ),
    },
    {
      header: t("ipmaStations.table.evaporationSource"),
      accessorKey: "evaporationSourceId",
      sortable: false,
      className: "min-w-[180px]",
      cell: (_: string, row: IPMAStation) => (
        <div>{infoSourceMap[row.evaporationSourceId] || ""}</div>
      ),
    },
  ];

  const actionsColumn: Column<IPMAStation> = {
    header: t("ipmaStations.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_value: string, row: IPMAStation) => (
      <div className="flex gap-2 justify-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => onEdit(row)} className="h-8 w-8 hover:bg-gray-100">
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("ipmaStations.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onArchiveAction(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                {!row.isArchived ? (
                  <Archive className="h-4 w-4 text-gray-500" />
                ) : (
                  <ArchiveRestore className="h-4 w-4 text-gray-500" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{!row.isArchived ? t("ipmaStations.actions.archive") : t("ipmaStations.actions.restore")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
}; 